import React from 'react';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useGetPositionsForIncident,
  useCreateNewPositionForIncident,
  useDeletePositionForIncident,
  useEditPositionForIncident,
} from 'hooks/incident/useRoster';
import { useForm } from 'react-hook-form';
import AllowWorkAssignmentField from './roster/AllowWorkAssignmentField';
import {
  OPERATIONS_SECTION_CHIEF_NAME,
  allowsWorkAssignmentByDefault,
} from './roster/allowWorkAssignment';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from './roster/SearchableSelect';

const ROSTER_READONLY_SEATS = new Set<string>([
  'incident commander',
  'safety officer',
  'public information officer',
  'liaison officer',
  'planning section chief',
  'resource unit leader',
  'situation unit leader',
  'environmental unit leader',
]);

type IncidentNewMemberDialogProps = {
  show: boolean;
  onClose: () => void;
};

type ActionValue = 'add' | 'edit' | 'delete';

const ACTION_OPTIONS: { value: ActionValue; label: string }[] = [
  { value: 'add', label: 'Add Seat' },
  { value: 'edit', label: 'Edit Seat' },
  { value: 'delete', label: 'Delete Seat' },
];

export default function IncidentNewMemberDialog({
  show = true,
  onClose,
}: IncidentNewMemberDialogProps) {
  const {
    clearErrors,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({ mode: 'onChange' });

  const incidentPositionsQuery = useGetPositionsForIncident();
  const incidentPositions = incidentPositionsQuery.data || [];

  // Renaming or deleting a resource's placement belongs to the resource modal, not to seat management. Resource
  // positions stay available as "Reports to" targets, since a seat may sit under one.
  const editableSeats = incidentPositions.filter(
    (position) => !position.isResourcePosition
  );

  const { mutate: createNew } = useCreateNewPositionForIncident();
  const { mutate: editPosition } = useEditPositionForIncident();
  const { mutate: deletePosition } = useDeletePositionForIncident();

  const [selectedSeat, setSelectedSeat] =
    React.useState<SearchableSelectOption | null>(null);
  const [selectedManagerSeat, setSelectedManagerSeat] =
    React.useState<SearchableSelectOption | null>(null);
  const [seatName, setSeatName] = React.useState<string>('');
  const [selectedAction, setSelectedAction] =
    React.useState<ActionValue | null>(null);

  const [isWorkgroup, setIsWorkgroup] = React.useState<boolean>(false);

  const seatOptions = editableSeats
    .filter(({ is_template }) => !is_template)
    .map(({ id, name }) => ({
      value: id,
      label: name,
    }));

  const managerOptions = incidentPositions.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  const resetActionFields = () => {
    setSeatName('');
    setSelectedManagerSeat(null);
    setSelectedSeat(null);
    clearErrors('seatName');
    setIsWorkgroup(false);
  };

  const onManageSeatSubmit = () => {
    switch (selectedAction) {
      case 'add':
        if (ROSTER_READONLY_SEATS.has(seatName.trim().toLowerCase())) {
          setError('seatName', {
            type: 'manual',
            message: 'This seat name is protected and cannot be duplicated.',
          });
          return;
        }
        if (!seatName.trim()) {
          setError('seatName', {
            type: 'required',
            message: 'Seat name is required',
          });
          return;
        }
        createNew({
          name: seatName,
          reportsTo: selectedManagerSeat?.value,
          isWorkgroup,
        });
        break;
      case 'edit':
        if (ROSTER_READONLY_SEATS.has(seatName.trim().toLowerCase())) {
          setError('seatName', {
            type: 'manual',
            message: 'This seat name is protected and cannot be modified.',
          });
          return;
        }
        if (!selectedSeat) {
          setError('seat', {
            type: 'required',
            message: 'Seat selection is required',
          });
        }
        if (!seatName.trim()) {
          setError('seatName', {
            type: 'required',
            message: 'Seat name is required',
          });
        }
        if (errors?.managerSeat?.message || !selectedSeat || !seatName.trim()) {
          return;
        }
        editPosition({
          id: selectedSeat?.value,
          name: seatName,
          reportsTo: selectedManagerSeat?.value,
          isWorkgroup,
        });
        break;
      case 'delete':
        if (!selectedSeat) {
          setError('seat', {
            type: 'required',
            message: 'Seat selection is required',
          });
          return;
        }
        deletePosition({
          id: selectedSeat?.value,
        });
        break;
    }

    onClose();
  };

  const checkForCircularParentage = (
    parentId: string | undefined,
    depth: number = 0
  ): boolean => {
    if (depth > 10 || !parentId) return false;
    if (parentId === selectedSeat?.value) return true;
    const parent = incidentPositions.find(({ id }) => id === parentId);
    return checkForCircularParentage(parent?.parent_id ?? undefined, depth + 1);
  };

  const setManagerSeatBasedOnSelectedSeat = (seatId: string) => {
    const selected = incidentPositions.find(({ id }) => id === seatId);
    if (selected && selected.parent_id) {
      setSelectedManagerSeat(
        managerOptions.find((option) => option.value === selected.parent_id) ??
          null
      );
    } else {
      setSelectedManagerSeat(null);
    }
  };

  const setIsWorkgroupBasedOnSelectedSeat = (seatId: string) => {
    const selected = incidentPositions.find(({ id }) => id === seatId);
    if (selected) {
      setIsWorkgroup(selected.is_workgroup || false);
    } else {
      setIsWorkgroup(false);
    }
  };

  return (
    <FormModal
      open={show}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Manage Custom Incident Seats"
      formProps={{ onSubmit: handleSubmit(onManageSeatSubmit) }}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button type="submit">Save Changes</Button>
        </>
      }
    >
      <div className="tw:space-y-4">
        <Select
          value={selectedAction}
          onValueChange={(value) => {
            setSelectedAction(value as ActionValue);
            resetActionFields();
          }}
        >
          <SelectTrigger className="tw:w-full" aria-label="Select Action">
            <SelectValue placeholder="Select Action" />
          </SelectTrigger>
          <SelectContent className="dark">
            {ACTION_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedAction === 'add' && (
          <>
            <div className="tw:space-y-2">
              <Label htmlFor="new-seat-name">New Seat Name</Label>
              <Input
                id="new-seat-name"
                placeholder="Enter new seat name"
                value={seatName}
                aria-invalid={!!errors.seatName}
                onChange={(e) => {
                  setSeatName(e.target.value);
                  if (e.target.value.trim() === OPERATIONS_SECTION_CHIEF_NAME) {
                    setIsWorkgroup(true);
                  }
                  if (!e.target.value.trim() || errors.seatName) {
                    clearErrors('seatName');
                  }
                }}
                onBlur={() => {
                  if (!seatName.trim()) {
                    clearErrors('seatName');
                    setError('seatName', {
                      type: 'required',
                      message: 'Seat name is required',
                    });
                  }
                }}
              />
              {errors.seatName ? (
                <p className="tw:text-sm tw:text-destructive">
                  {errors.seatName.message as string}
                </p>
              ) : null}
            </div>
            <SearchableSelect
              id="new-seat-reports-to"
              label="Reports to:"
              options={managerOptions}
              value={selectedManagerSeat}
              onChange={(value) => {
                setSelectedManagerSeat(value);
                clearErrors('seat');
                setIsWorkgroup(
                  allowsWorkAssignmentByDefault(
                    value?.value,
                    incidentPositions,
                    seatName
                  )
                );
              }}
              placeholder="Select a seat"
              clearable
            />
            <AllowWorkAssignmentField
              id="new-seat-workgroup"
              checked={isWorkgroup}
              onCheckedChange={setIsWorkgroup}
            />
          </>
        )}
        {selectedAction === 'edit' && (
          <>
            <SearchableSelect
              id="edit-seat-select"
              label="Select Seat"
              options={seatOptions}
              value={selectedSeat}
              onChange={(option) => {
                setSelectedSeat(option);
                setSeatName(option?.label ?? '');
                clearErrors('seat');
                clearErrors('seatName');
                if (option) {
                  setManagerSeatBasedOnSelectedSeat(option.value);
                  setIsWorkgroupBasedOnSelectedSeat(option.value);
                } else {
                  setSelectedManagerSeat(null);
                  setIsWorkgroup(false);
                }
              }}
              placeholder="Select a seat"
              clearable
            />
            <div className="tw:space-y-2">
              <Label htmlFor="edit-seat-name">New Seat Name</Label>
              <Input
                id="edit-seat-name"
                placeholder="Enter new seat name"
                value={seatName}
                aria-invalid={!!errors.seatName}
                onChange={(e) => {
                  setSeatName(e.target.value);
                  if (!e.target.value.trim() || errors.seatName) {
                    clearErrors('seatName');
                  }
                }}
                onBlur={() => {
                  if (!seatName.trim()) {
                    clearErrors('seatName');
                    setError('seatName', {
                      type: 'required',
                      message: 'Seat name is required',
                    });
                  }
                }}
              />
              {errors.seatName ? (
                <p className="tw:text-sm tw:text-destructive">
                  {errors.seatName.message as string}
                </p>
              ) : null}
            </div>
            <div>
              <SearchableSelect
                id="edit-seat-reports-to"
                label="Reports to:"
                options={managerOptions}
                value={selectedManagerSeat}
                onChange={(value) => {
                  if (checkForCircularParentage(value?.value)) {
                    setError('managerSeat', {
                      type: 'manual',
                      message:
                        'Circular parentage detected. Please select a different manager.',
                    });
                  } else {
                    clearErrors('managerSeat');
                  }
                  setSelectedManagerSeat(value);
                }}
                placeholder="Select a manager seat"
                clearable
              />
              {errors?.managerSeat?.message ? (
                <p className="tw:mt-1 tw:text-sm tw:text-destructive">
                  {errors.managerSeat.message as string}
                </p>
              ) : null}
            </div>
            <AllowWorkAssignmentField
              id="edit-seat-workgroup"
              checked={isWorkgroup}
              onCheckedChange={setIsWorkgroup}
            />
          </>
        )}
        {selectedAction === 'delete' && (
          <SearchableSelect
            id="delete-seat-select"
            label="Select Seat"
            options={seatOptions}
            value={selectedSeat}
            onChange={(value) => {
              setSelectedSeat(value);
              clearErrors('seat');
            }}
            placeholder="Select a seat"
            clearable
          />
        )}
      </div>
    </FormModal>
  );
}
